import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, X, Check } from "lucide-react";
import {
  adminListReferralUsers,
  adminGetReferralUserDetail,
  adminListPayouts,
  adminGetPayoutDetail,
  adminApprovePayout,
  adminMarkPayoutPaid,
  adminRejectPayout,
} from "../api/referralApi";
import { formatApiError } from "../api/client";

/**
 * Admin Referrals — Phase F.
 *
 * Two tabs:
 *  - Users: paginated, sortable list with referral metrics.
 *  - Payouts: filter by status, click row → modal with decrypted IBAN +
 *    approve / mark-paid / reject actions.
 */

const eur = (n) => `${(Number(n) || 0).toFixed(2).replace(".", ",")} €`;

function StatusBadge({ status }) {
  const map = {
    pending: "border-amber-500/30 text-amber-300 bg-amber-500/10",
    approved: "border-sky-500/30 text-sky-300 bg-sky-500/10",
    paid: "border-emerald-500/30 text-emerald-300 bg-emerald-500/10",
    rejected: "border-rose-500/30 text-rose-300 bg-rose-500/10",
  };
  return (
    <span
      className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${
        map[status] || map.pending
      }`}
    >
      {status}
    </span>
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const [hasPayable, setHasPayable] = useState(false);
  const [sort, setSort] = useState("payable_desc");
  // Phase-F follow-up — clicking a row opens the user-detail modal with
  // the full commission ledger. Backend endpoint was already serving;
  // this only wires the UI trigger.
  const [selectedUserUlid, setSelectedUserUlid] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "referrals", "users", q, hasPayable, sort],
    queryFn: () =>
      adminListReferralUsers({
        q: q || undefined,
        has_payable: hasPayable || undefined,
        sort,
        limit: 100,
      }),
  });

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="admin-referrals-users-tab">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Email suchen…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="admin-ref-users-search"
          className="flex-1 min-w-[200px] rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
        />
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={hasPayable}
            onChange={(e) => setHasPayable(e.target.checked)}
            data-testid="admin-ref-users-haspayable"
            className="h-4 w-4"
          />
          Nur mit Guthaben
        </label>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="admin-ref-users-sort"
          className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100"
        >
          <option value="payable_desc">Auszahlbar (absteigend)</option>
          <option value="paid_desc">Bereits ausgezahlt (absteigend)</option>
          <option value="invited_desc">Meiste Einladungen</option>
          <option value="created_desc">Neueste zuerst</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-900/30">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Eingeladen von</th>
              <th className="px-4 py-3 text-right">Einladungen</th>
              <th className="px-4 py-3 text-right">Aktiv zahlend</th>
              <th className="px-4 py-3 text-right">Ausstehend</th>
              <th className="px-4 py-3 text-right">Auszahlbar</th>
              <th className="px-4 py-3 text-right">Ausgezahlt</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-zinc-500">Laden…</td></tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-zinc-500">Keine Nutzer gefunden.</td></tr>
            )}
            {items.map((u) => (
              <tr
                key={u.user_ulid}
                data-testid={`admin-ref-user-${u.user_ulid}`}
                onClick={() => setSelectedUserUlid(u.user_ulid)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedUserUlid(u.user_ulid);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Details für ${u.email} öffnen`}
                className="cursor-pointer border-b border-zinc-900/60 transition-colors last:border-0 hover:bg-zinc-800/50 focus:bg-zinc-800/60 focus:outline-none"
              >
                <td className="px-4 py-3 text-zinc-200">{u.email}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{u.referral_code}</td>
                <td className="px-4 py-3 text-xs text-zinc-400">{u.referred_by_email || "—"}</td>
                <td className="px-4 py-3 text-right text-zinc-300">{u.invited_count}</td>
                <td className="px-4 py-3 text-right text-emerald-300">{u.active_paid_referrals}</td>
                <td className="px-4 py-3 text-right text-amber-300">{eur(u.pending_eur)}</td>
                <td className="px-4 py-3 text-right text-emerald-300">{eur(u.payable_eur)}</td>
                <td className="px-4 py-3 text-right text-zinc-300">{eur(u.paid_eur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserDetailModal
        userUlid={selectedUserUlid}
        onClose={() => setSelectedUserUlid(null)}
        onOpenReferrer={(ulid) => setSelectedUserUlid(ulid)}
      />
    </div>
  );
}

function UserDetailModal({ userUlid, onClose, onOpenReferrer }) {
  // The endpoint returns up to 500 ledger rows in one shot; we paginate
  // client-side at 20 per page. No cursor wiring needed.
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  // Reset paging whenever the modal switches user (e.g. user clicks
  // "Eingeladen von" → opens the referrer's modal in-place).
  React.useEffect(() => {
    setPage(0);
  }, [userUlid]);

  // Close on Esc.
  React.useEffect(() => {
    if (!userUlid) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [userUlid, onClose]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "referrals", "user", userUlid],
    queryFn: () => adminGetReferralUserDetail(userUlid),
    enabled: Boolean(userUlid),
  });

  if (!userUlid) return null;

  const ledger = data?.ledger || [];
  const totalPages = Math.max(1, Math.ceil(ledger.length / PAGE_SIZE));
  const pageRows = ledger.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Build a quick ulid → email map so we can label referred users
  // (and the inviter link) without an extra round-trip.
  const invitedEmailByUlid = {};
  for (const inv of data?.invited_users || []) {
    if (inv?.ulid) invitedEmailByUlid[inv.ulid] = inv.email;
  }

  const user = data?.user || {};
  const balance = data?.balance || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-ref-user-detail-modal"
    >
      <button
        type="button"
        aria-label="Schließen"
        onClick={onClose}
        className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm"
      />
      <div className="relative max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          data-testid="admin-ref-user-detail-close"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-md border border-zinc-800 text-zinc-400 hover:bg-zinc-900"
        >
          <X size={14} />
        </button>

        <div className="max-h-[90vh] overflow-y-auto p-6">
          {isLoading && <div className="py-8 text-center text-zinc-500">Laden…</div>}
          {error && (
            <div className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300">
              {formatApiError(error)}
            </div>
          )}
          {data && (
            <>
              <header className="pr-10">
                <h2 className="text-lg font-semibold text-zinc-100">{user.email || "—"}</h2>
                <p className="mt-1 font-mono text-xs text-zinc-500">{user.ulid}</p>
              </header>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Referral-Code</div>
                  <div className="mt-1 font-mono text-sm text-zinc-200">{user.referral_code || "—"}</div>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Beigetreten</div>
                  <div className="mt-1 text-sm text-zinc-200">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleString("de-DE")
                      : "—"}
                  </div>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Eingeladen von</div>
                  <div className="mt-1 text-sm">
                    {user.referred_by_user_id ? (
                      <button
                        type="button"
                        onClick={() => onOpenReferrer(user.referred_by_user_id)}
                        data-testid="admin-ref-user-detail-open-referrer"
                        className="font-mono text-xs text-emerald-300 underline-offset-4 hover:underline"
                        title="Inviter-Detail öffnen"
                      >
                        {user.referred_by_user_id}
                      </button>
                    ) : (
                      <span className="text-zinc-500">—</span>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                    Eingeladen / Aktiv zahlend
                  </div>
                  <div className="mt-1 text-sm text-zinc-200">
                    {(data.invited_users || []).length}{" "}
                    <span className="text-zinc-500">/</span>{" "}
                    <span className="text-emerald-300">
                      {balance.estimated_monthly_eur != null ? "€/mo " : ""}
                      {eur(balance.estimated_monthly_eur)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Ausstehend</div>
                  <div className="mt-1 text-base font-semibold text-amber-300">{eur(balance.pending_eur)}</div>
                </div>
                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Auszahlbar</div>
                  <div className="mt-1 text-base font-semibold text-emerald-300">{eur(balance.payable_eur)}</div>
                </div>
                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Ausgezahlt</div>
                  <div className="mt-1 text-base font-semibold text-zinc-200">{eur(balance.paid_eur)}</div>
                </div>
                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Storniert</div>
                  <div className="mt-1 text-base font-semibold text-rose-300">{eur(balance.reversed_eur)}</div>
                </div>
              </div>

              <div className="mt-5">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                    Provisions-Ledger ({ledger.length})
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0}
                        data-testid="admin-ref-user-detail-prev-page"
                        className="rounded-md border border-zinc-800 px-2 py-1 hover:bg-zinc-900 disabled:opacity-40"
                      >
                        ←
                      </button>
                      <span className="text-zinc-500">
                        {page + 1} / {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        data-testid="admin-ref-user-detail-next-page"
                        className="rounded-md border border-zinc-800 px-2 py-1 hover:bg-zinc-900 disabled:opacity-40"
                      >
                        →
                      </button>
                    </div>
                  )}
                </div>
                {ledger.length === 0 ? (
                  <div className="rounded-md border border-zinc-800/60 bg-zinc-900/30 px-4 py-6 text-center text-xs text-zinc-500">
                    Noch keine Provisionen für diesen Nutzer.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-zinc-800/60 bg-zinc-900/30">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-zinc-800/60 text-left text-zinc-500">
                          <th className="px-3 py-2">Erstellt</th>
                          <th className="px-3 py-2">Level</th>
                          <th className="px-3 py-2">Empfohlener</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Periode</th>
                          <th className="px-3 py-2 text-right">EUR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((c) => (
                          <tr
                            key={c.ulid}
                            data-testid={`admin-ref-ledger-${c.ulid}`}
                            className="border-b border-zinc-900/40 last:border-0"
                          >
                            <td className="px-3 py-1.5 text-zinc-400">
                              {c.created_at ? new Date(c.created_at).toLocaleDateString("de-DE") : "—"}
                            </td>
                            <td className="px-3 py-1.5 text-zinc-300">L{c.level}</td>
                            <td className="px-3 py-1.5 font-mono text-[10px] text-zinc-400">
                              {invitedEmailByUlid[c.referred_user_ulid] || c.referred_user_ulid}
                            </td>
                            <td className="px-3 py-1.5"><StatusBadge status={c.status} /></td>
                            <td className="px-3 py-1.5 text-zinc-400">
                              {c.period_start && c.period_end
                                ? `${new Date(c.period_start).toLocaleDateString("de-DE")} – ${new Date(c.period_end).toLocaleDateString("de-DE")}`
                                : "—"}
                            </td>
                            <td className="px-3 py-1.5 text-right text-zinc-200">{eur(c.commission_eur)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PayoutDetailModal({ payoutUlid, onClose }) {
  const qc = useQueryClient();
  const [adminNote, setAdminNote] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [copiedIban, setCopiedIban] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "referrals", "payout", payoutUlid],
    queryFn: () => adminGetPayoutDetail(payoutUlid),
    enabled: Boolean(payoutUlid),
  });

  const approve = useMutation({
    mutationFn: () => adminApprovePayout(payoutUlid, { admin_note: adminNote }),
    onSuccess: () => {
      toast.success("Auszahlung genehmigt");
      qc.invalidateQueries({ queryKey: ["admin", "referrals"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const markPaid = useMutation({
    mutationFn: () =>
      adminMarkPayoutPaid(payoutUlid, {
        admin_note: adminNote,
        payment_reference: paymentRef || null,
      }),
    onSuccess: () => {
      toast.success("Als bezahlt markiert");
      qc.invalidateQueries({ queryKey: ["admin", "referrals"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const reject = useMutation({
    mutationFn: () => adminRejectPayout(payoutUlid, { admin_note: adminNote }),
    onSuccess: () => {
      toast.success("Auszahlung abgelehnt — Provisionen zurückgesetzt");
      qc.invalidateQueries({ queryKey: ["admin", "referrals"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (!payoutUlid) return null;

  const copyIban = async (iban) => {
    try {
      await navigator.clipboard.writeText(iban);
      setCopiedIban(true);
      setTimeout(() => setCopiedIban(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const canApprove = data?.status === "pending";
  const canMarkPaid = data?.status === "pending" || data?.status === "approved";
  const canReject = data?.status === "pending" || data?.status === "approved";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-payout-detail-modal"
    >
      <button
        type="button"
        aria-label="Schließen"
        onClick={onClose}
        className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-2xl rounded-xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          data-testid="admin-payout-modal-close"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md border border-zinc-800 text-zinc-400 hover:bg-zinc-900"
        >
          <X size={14} />
        </button>
        {isLoading && <div className="py-8 text-center text-zinc-500">Laden…</div>}
        {error && (
          <div className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300">
            {formatApiError(error)}
          </div>
        )}
        {data && (
          <>
            <header className="flex items-start justify-between gap-3 pr-8">
              <div>
                <h2 className="text-lg font-semibold text-zinc-100">Auszahlung</h2>
                <p className="mt-1 text-xs text-zinc-500">
                  {data.user?.email} · <StatusBadge status={data.status} />
                </p>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Betrag</div>
                <div className="text-2xl font-semibold text-emerald-300">{eur(data.requested_eur)}</div>
              </div>
            </header>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">IBAN</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code
                    data-testid="admin-payout-iban"
                    className="block break-all font-mono text-sm text-zinc-200"
                  >
                    {data.iban || "—"}
                  </code>
                  {data.iban && (
                    <button
                      type="button"
                      onClick={() => copyIban(data.iban)}
                      data-testid="admin-payout-iban-copy"
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-md border border-zinc-800 text-zinc-400 hover:bg-zinc-900"
                      aria-label="IBAN kopieren"
                    >
                      {copiedIban ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Kontoinhaber</div>
                <div className="mt-1 text-sm text-zinc-200">{data.account_holder_name}</div>
              </div>
              {data.user_note && (
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Notiz vom Nutzer</div>
                  <div className="mt-1 text-sm text-zinc-200">{data.user_note}</div>
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                Provisionen ({(data.commissions || []).length})
              </div>
              <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-zinc-800/60 bg-zinc-900/30">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-800/60 text-left text-zinc-500">
                      <th className="px-3 py-2">Datum</th>
                      <th className="px-3 py-2">Level</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">EUR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.commissions || []).map((c) => (
                      <tr key={c.ulid} className="border-b border-zinc-900/40 last:border-0">
                        <td className="px-3 py-1.5 text-zinc-400">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString("de-DE") : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-zinc-300">L{c.level}</td>
                        <td className="px-3 py-1.5 text-zinc-300">{c.status}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-200">{eur(c.commission_eur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <textarea
                rows={2}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Admin-Notiz (Pflichtfeld bei Ablehnung)"
                data-testid="admin-payout-note"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
              />
              <input
                type="text"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="Zahlungsreferenz (für Mark-Paid)"
                data-testid="admin-payout-paymentref"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 font-mono text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!canApprove || approve.isPending}
                  onClick={() => approve.mutate()}
                  data-testid="admin-payout-approve-btn"
                  className="flex-1 rounded-md border border-sky-500/40 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/10 disabled:opacity-40"
                >
                  Genehmigen
                </button>
                <button
                  type="button"
                  disabled={!canMarkPaid || markPaid.isPending}
                  onClick={() => markPaid.mutate()}
                  data-testid="admin-payout-markpaid-btn"
                  className="flex-1 rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
                >
                  Als bezahlt markieren
                </button>
                <button
                  type="button"
                  disabled={!canReject || reject.isPending || !adminNote.trim()}
                  onClick={() => reject.mutate()}
                  data-testid="admin-payout-reject-btn"
                  className="flex-1 rounded-md border border-rose-500/40 px-4 py-2 text-sm text-rose-200 hover:bg-rose-500/10 disabled:opacity-40"
                  title={!adminNote.trim() ? "Notiz erforderlich für Ablehnung" : ""}
                >
                  Ablehnen
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PayoutsTab() {
  const [status, setStatus] = useState("pending");
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "referrals", "payouts", status],
    queryFn: () => adminListPayouts({ status, limit: 100 }),
  });

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="admin-referrals-payouts-tab">
      <div className="flex flex-wrap gap-2">
        {["pending", "approved", "paid", "rejected"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            data-testid={`admin-payouts-filter-${s}`}
            className={`rounded-md border px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
              status === s
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-zinc-800 text-zinc-400 hover:bg-zinc-900"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-900/30">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-3">Nutzer</th>
              <th className="px-4 py-3">Betrag</th>
              <th className="px-4 py-3">Konto</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Angefordert</th>
              <th className="px-4 py-3 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-500">Laden…</td></tr>}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-500">Keine Auszahlungen mit Status "{status}".</td></tr>
            )}
            {items.map((p) => (
              <tr
                key={p.ulid}
                data-testid={`admin-payout-row-${p.ulid}`}
                className="border-b border-zinc-900/60 last:border-0 hover:bg-zinc-900/40"
              >
                <td className="px-4 py-3 text-zinc-200">{p.user_email}</td>
                <td className="px-4 py-3 font-semibold text-emerald-300">{eur(p.requested_eur)}</td>
                <td className="px-4 py-3 text-xs text-zinc-400">{p.account_holder_name}</td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-xs text-zinc-400">
                  {p.requested_at ? new Date(p.requested_at).toLocaleString("de-DE") : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => setSelected(p.ulid)}
                    data-testid={`admin-payout-open-${p.ulid}`}
                    className="rounded-md border border-zinc-700 px-3 py-1 text-xs text-zinc-200 hover:bg-zinc-800"
                  >
                    Öffnen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PayoutDetailModal payoutUlid={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default function AdminReferralsPage() {
  const [tab, setTab] = useState("users");
  return (
    <div className="space-y-6" data-testid="admin-referrals-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Referrals</h1>
        <p className="text-sm text-zinc-500">Nutzer-Übersicht & manuelle Auszahlungen.</p>
      </header>

      <div className="flex gap-1 border-b border-zinc-800" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "users"}
          onClick={() => setTab("users")}
          data-testid="admin-referrals-tab-users"
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            tab === "users"
              ? "border-b-2 border-emerald-400 text-zinc-100"
              : "text-zinc-500 hover:text-zinc-200"
          }`}
        >
          Nutzer
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "payouts"}
          onClick={() => setTab("payouts")}
          data-testid="admin-referrals-tab-payouts"
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            tab === "payouts"
              ? "border-b-2 border-emerald-400 text-zinc-100"
              : "text-zinc-500 hover:text-zinc-200"
          }`}
        >
          Auszahlungsanfragen
        </button>
      </div>

      {tab === "users" ? <UsersTab /> : <PayoutsTab />}
    </div>
  );
}
